import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ActivityIcon } from '../../../../assets/icons/activety-icon.svg';
import { ReactComponent as CardPosIcon } from '../../../../assets/icons/card-pos-icon.svg';
import { ReactComponent as EmailIcon } from '../../../../assets/icons/email-icon.svg';
import { ReactComponent as MoreIcon } from '../../../../assets/icons/more-icon.svg';
import { ReactComponent as PhoneIcon } from '../../../../assets/icons/phone-icon.svg';
import { ReactComponent as ResetPasswordIcon } from '../../../../assets/icons/reset-password-v1-icon.svg';
import { ReactComponent as TrendDownIcon } from '../../../../assets/icons/trend-down.svg';
import { ReactComponent as UserIcon } from '../../../../assets/icons/user-icon.svg';
import { ReactComponent as UserTagIcon } from '../../../../assets/icons/user-tag.svg';
import profile from '../../../../assets/images/profiles/profile.jpg';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { fetchBasicInformations } from '../../../../redux/slices/EndConsumerSlice';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../../shared-components/button/Button';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import CustomPhoneInput from '../../../../shared-components/custom-input/CustomPhoneInput';
import DropDownMenu from '../../../../shared-components/drop-down/DropDownMenu';
import ConfirmBox from '../../../../shared-components/modal-box/ConfirmBox';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import userApiV2 from '../../../../utils/api/end-consumer-v2';
import userApiV3 from '../../../../utils/api/v2/end-consumer';
import WalletHistory from '../SidePaneComponents/WalletHistory/WalletHistory';

import ActivityTimeline from './ActivityTimeline';
import AddWallet from './AddWallet';

interface MenuList {
  id: string;
  content: JSX.Element;
  allow: boolean;
  function: () => void;
  disabled: boolean;
}

/**
 * Interface representing a profile stat card configuration item
 */
interface ProfileStatCardItem {
  key: string;
  label: string;
  value: string;
  icon: React.ReactNode;
  theme: string;
}

/**
 * Component to display end consumer profile
 * @returns {any} - The ProfileConsumer component
 */
export default function ProfileConsumer() {
  const { t, i18n } = useTranslation('endConsumer');
  const dispatch = useAppDispatch();
  const [consumerStatus, setConsumerStatus] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);

  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    countryCode: '',
    accountStatus: '',
    isActive: false,
    wallet: { balance: '0', deposit: '0', currency: '' },
  });

  const [originalForm, setOriginalForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    countryCode: '',
    accountStatus: '',
    isActive: false,
    wallet: { balance: '0', deposit: '0', currency: '' },
  });

  const { loading, selectedEndConsumer, basicInformation } = useAppSelector(
    (state) => state.endConsumerType,
  );

  const [modalView, setModalView] = useState({
    status: false,
    title: t('addAccount'),
    header: true,
    content: <div />,
    rightCorner: '',
  });
  const [confirmBox, setConfirmBox] = useState({
    status: false,
    title: '',
    subTitle: '',
    submitLabel: t('ok'),
    /**
     * Demo function
     */
    conformSubmit: () => { },
  });

  const [isModelOpen, setIsModelOpen] = useState<boolean>(false);
  const [modelData, setModelData] = useState<{
    title: string;
    content: React.ReactElement;
    width?: string | number;
  }>({
    title: '',
    content: <div />,
  });

  /**
   * Fetch Users list function
   * @param {any} userCustomId - The userCustomId
   */
  const fetchData = (userCustomId: string) => {
    if (!loading) {
      dispatch(fetchBasicInformations({ userCustomId }));
    }
  };

  /**
   * Modal box close function
   */
  const closeModal = () => {
    setIsEditMode(false);
    setModalView({
      status: false,
      title: '',
      header: false,
      content: <div />,
      rightCorner: '',
    });
  };

  /**
   * Sends a password reset link to the user's email for a specific consumer.
   * @param {string} currentData - The ID of the consumer whose password is to be reset.
   */
  const confirmResetPassword = (currentData: string) => {
    userApiV3
      .resetPassword(currentData, { submissionStatus: 'userCustomId' })
      .then(() => {
        setConfirmBox((previous) => ({ ...previous, status: false }));
        setModalView({
          status: true,
          title: '',
          header: false,
          content: (
            <SuccessNotification
              title={t('passwordResetSuccessNotification')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                closeModal();
              }}
            />
          ),
          rightCorner: '',
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            message:
              error?.response?.data?.message ||
              `Password reset failed for Consumer ID: ${currentData}.`,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Displays a confirmation prompt to reset the password for a specific consumer.
   */
  const resetPasswordFunction = () => {
    if (selectedEndConsumer?.userCustomId) {
      setConfirmBox({
        status: true,
        title: t('resetPasswordTitle'),
        subTitle: t('resetPasswordMessage'),
        /**
         * Triggers the password reset process for the specified consumer.
         * @returns {void} The result
         */
        conformSubmit: () => confirmResetPassword(selectedEndConsumer?.userCustomId), // ✅ Now correctly calls the function
        submitLabel: t('proceed'),
      });
    }
  };
  /**
   * Open Activity Log side panel
   */
  const handleLogActivity = () => {
    setIsModelOpen(true);
    setModelData({
      title: t('activityLog'),
      content: <ActivityTimeline userCustomId={selectedEndConsumer?.userCustomId} />,
    });
  };

  // /**
  //  * handle Debit
  //  */
  // const handleDebit = () => {
  //   setIsModelOpen(true);
  //   setModelData({
  //     title: 'Debit Form',
  //     content: <DebitForm />,
  //   });
  // };

  // /**
  //  * handle Refund
  //  */
  // const handleRefund = () => {
  //   setIsModelOpen(true);
  //   setModelData({
  //     title: 'Refund Form',
  //     content: <RefundForm />,
  //   });
  // };

  /**
   * Open Wallet History side panel
   */
  const renderWalletHistory = () => {
    setIsModelOpen(true);
    setModelData({
      title: t('walletHistory'),
      content: <WalletHistory />,
      width: '33vw',
    });
  };

  /**
   * Populate edit form when entering edit mode
   */
  const handleProfileEditInformation = async () => {
    if (!selectedEndConsumer?.userCustomId) return;

    try {
      const response = await userApiV3.fetchBasicProfile(selectedEndConsumer.userCustomId);
      const fetchedCountryCode =
        response?.data?.countryCode ||
        response?.data?.user?.countryCode ||
        (basicInformation?.user as any)?.countryCode ||
        '966';

      const updatedData = {
        ...response.data,
        countryCode: fetchedCountryCode,
      };

      setOriginalForm(updatedData);
      setEditForm(updatedData);
      setConsumerStatus(response.data.accountStatus);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('updateFailed'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Handle input change
   */
  const handleChange = useCallback((key: keyof any, value: string) => {
    setEditForm((previous: any) => ({ ...previous, [key]: value }));
  }, []);
  /**
   * Save edited profile or update account status
   */
  const handleSave = useCallback(
    async (actionType: 'edit' | 'VIP' | 'ONHOLD' | 'BLOCKED' | 'RESUMED') => {
      if (!selectedEndConsumer?.userCustomId) return;

      let payload: Record<string, string> = {};

      switch (actionType) {
        case 'edit': {
          payload = {
            firstName: editForm.firstName,
            lastName: editForm.lastName,
            email: editForm.email,
            phoneNumber: editForm.phoneNumber,
            countryCode: editForm.countryCode || originalForm.countryCode || '966',
          };
          break;
        }

        case 'VIP':
          payload = {
            accountStatus: 'VIP',
          };
          break;

        case 'ONHOLD':
          payload = {
            accountStatus: 'ONHOLD',
          };
          break;

        case 'BLOCKED':
          payload = {
            accountStatus: 'BLOCKED',
          };
          break;

        case 'RESUMED':
          payload = {
            accountStatus: 'RESUMED',
          };
          break;

        default:
          return;
      }

      try {
        if (actionType === 'edit' && Object.keys(payload).length === 0) {
          setEditForm(originalForm);
          setIsEditMode(false);
          return;
        }

        setIsSaveLoading(true);

        await userApiV2.editBasicInformation(selectedEndConsumer.userCustomId, payload);
        setConsumerStatus(actionType);
        handleProfileEditInformation();

        if (actionType === 'edit') {
          setModalView({
            status: true,
            title: '',
            header: false,
            content: (
              <SuccessNotification
                title={t('consumerUpdatedSuccessfully')}
                description=""
                notificationCloseTimer={3000}
                onNotificationClose={() => {
                  closeModal();
                }}
              />
            ),
            rightCorner: '',
          });
        } else {
          dispatch(
            setNotificationMessage({
              message: t('consumerUpdatedSuccessfully'),
              status: true,
              type: 'success',
              code: 200,
            }),
          );
        }

        setIsEditMode(false);

        // Refresh latest profile data
        dispatch(fetchBasicInformations({ userCustomId: selectedEndConsumer.userCustomId }));
      } catch (error: any) {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('updateFailed'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      } finally {
        setIsSaveLoading(false);
      }
    },
    [dispatch, editForm, originalForm, selectedEndConsumer],
  );

  /**
   * Cancel edit mode and restore original form values
   */
  const handleCancelEdit = useCallback(() => {
    setEditForm(originalForm);
    setIsEditMode(false);
    setModalView((previous) => ({ ...previous, status: false }));
  }, [originalForm]);

  /**
   * Populate edit form when entering edit mode
   */
  const startEditMode = useCallback(() => {
    setEditForm(originalForm);
    setIsEditMode(true);
  }, [originalForm]);
  const isHold = consumerStatus === 'ONHOLD';
  // const isPaused = consumerStatus === 'BLOCKED';

  const menuList: MenuList[] = [
    // {
    //   id: 'toggle-status',
    //   content: <span>{isPaused ? t('resume') : t('pause')}</span>,
    //   allow: true,
    //   disabled: isHold, // ONHOLD disables everything
    //   /**
    //    * Resume || pause
    //    * @returns {void} - Triggers handleSave
    //    */
    //   function: () => handleSave(isPaused ? 'RESUMED' : 'BLOCKED'),
    // },
    // {
    //   id: 'hold',
    //   content: <span>{t('hold')}</span>,
    //   allow: true,
    //   disabled: isHold || isPaused, // Disabled when ONHOLD or BLOCKED
    //   /**
    //    * ONHOLD
    //    * @returns {void} - Triggers handleSave
    //    */
    //   function: () => handleSave('ONHOLD'),
    // },
    // {
    //   id: 'vip',
    //   content: <span>{t('markAsVip')}</span>,
    //   allow: true,
    //   disabled: isHold || isPaused, // Disabled when ONHOLD or BLOCKED
    //   /**
    //    * ONHOLD
    //    * @returns {void} - Triggers handleSave
    //    */
    //   function: () => handleSave('VIP'),
    // },
    {
      id: 'edit',
      content: <span>{t('edit')}</span>,
      allow: true,
      disabled: isHold, // Disabled only when ONHOLD
      /**
       * ONHOLD
       * @returns {void} - Triggers startEditMode
       */
      function: () => startEditMode(),
    },
  ];

  /**
   * Refreshes user details and basic information after a successful wallet top-up.
   */
  const handleWalletSuccess = () => {
    if (selectedEndConsumer?.userCustomId) {
      fetchData(selectedEndConsumer.userCustomId);
      handleProfileEditInformation();
    }
  };

  /**
   * Handle open add money modal
   */
  const handleOpenAddMoneyModal = () => {
    const endConsumerId = selectedEndConsumer?.userCustomId;
    if (!endConsumerId) return;
    setModalView({
      status: true,
      title: t('addMoney'),
      content: (
        <AddWallet
          endConsumerId={endConsumerId}
          closeModal={closeModal}
          onSuccess={handleWalletSuccess}
          actionType="add"
        />
      ),
      header: true,
      rightCorner: '',
    });
  };

  /**
   * Handle open deduct money modal
   */
  const handleOpenDeductMoneyModal = () => {
    const endConsumerId = selectedEndConsumer?.userCustomId;
    if (!endConsumerId) return;
    setModalView({
      status: true,
      title: t('deductMoney'),
      content: (
        <AddWallet
          endConsumerId={endConsumerId}
          closeModal={closeModal}
          onSuccess={handleWalletSuccess}
          actionType="deduct"
        />
      ),
      header: true,
      rightCorner: '',
    });
  };

  useEffect(() => {
    if (selectedEndConsumer?.userCustomId) {
      fetchData(selectedEndConsumer.userCustomId);
      handleProfileEditInformation();
    }
  }, [selectedEndConsumer]);

  const hasOriginalPhone = Boolean(originalForm?.phoneNumber?.trim());
  const hasOriginalEmail = Boolean(originalForm?.email?.trim());
  const isPhoneInvalid = hasOriginalPhone && !editForm?.phoneNumber?.trim();
  const isEmailInvalid = hasOriginalEmail && !editForm?.email?.trim();
  const isSaveDisabled = isPhoneInvalid || isEmailInvalid;

  useEffect(() => {
    if (isEditMode) {
      setModalView({
        status: true,
        title: t('editBasicInformation', 'Edit Basic Information'),
        header: true,
        rightCorner: '',
        content: (
          <div className="edit-basic-info-modal">
            <div className="form-row">
              <div className="form-group">
                <p className="field-label">{t('firstName')}</p>
                <CustomInput
                  type="text"
                  name="firstName"
                  value={editForm.firstName || ''}
                  onChangeValue={(name, value) => handleChange(name, String(value))}
                  placeholder={t('firstName')}
                />
              </div>
              <div className="form-group">
                <p className="field-label">{t('lastName')}</p>
                <CustomInput
                  type="text"
                  name="lastName"
                  value={editForm.lastName || ''}
                  onChangeValue={(name, value) => handleChange(name, String(value))}
                  placeholder={t('lastName')}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <p className="field-label">{t('phone')}</p>
                <CustomPhoneInput
                  type="number"
                  name="phoneNumber"
                  placeholder={t('phoneNumber')}
                  value={editForm?.phoneNumber || ''}
                  value1={editForm?.countryCode || originalForm?.countryCode || '966'}
                  onChangeValue={(name, value) => handleChange(name, String(value))}
                  minLength={5}
                  maxLength={13}
                />
              </div>
              <div className="form-group">
                <p className="field-label">{t('email')}</p>
                <CustomInput
                  type="email"
                  name="email"
                  value={editForm.email || ''}
                  placeholder={t('enterEmail')}
                  onChangeValue={(name, value) => handleChange(name, String(value))}
                />
              </div>
            </div>
            <div className="modal-actions">
              <MyButton label={t('cancel')} buttonType="outline" onClickFunc={handleCancelEdit} />
              <MyButton
                label={t('save')}
                buttonType="submit"
                loading={isSaveLoading}
                disableButton={isSaveDisabled || isSaveLoading}
                onClickFunc={() => handleSave('edit')}
              />
            </div>
          </div>
        ),
      });
    }
  }, [
    isEditMode,
    editForm,
    isSaveLoading,
    isSaveDisabled,
    handleCancelEdit,
    handleChange,
    handleSave,
    t,
  ]);

  const consumerIdDisplay =
    (originalForm as any)?.userCustomId ||
    selectedEndConsumer?.userCustomId ||
    basicInformation?.user?.userCustomId ||
    '-';

  const profileStatsConfig: ProfileStatCardItem[] = [
    {
      key: 'name',
      label: t('name'),
      value:
        originalForm?.firstName || originalForm?.lastName
          ? `${originalForm?.firstName || ''} ${originalForm?.lastName || ''}`.trim()
          : '-',
      icon: <UserIcon />,
      theme: 'user',
    },
    {
      key: 'phone',
      label: t('phone'),
      value: originalForm?.phoneNumber
        ? `${originalForm?.countryCode ? `+${originalForm.countryCode} ` : ''}${originalForm.phoneNumber}`
        : '-',
      icon: <PhoneIcon />,
      theme: 'phone',
    },
    {
      key: 'consumerId',
      label: t('consumerId'),
      value: consumerIdDisplay,
      icon: <UserTagIcon />,
      theme: 'consumerId',
    },
    {
      key: 'email',
      label: t('email'),
      value: originalForm?.email || '-',
      icon: <EmailIcon />,
      theme: 'email',
    },
  ];

  return (
    <div className="ec-profile" dir={i18n.dir()}>
      {isModelOpen && (
        <SidePaneModel
          open={isModelOpen}
          title={modelData.title}
          content={modelData.content}
          closePanel={() => setIsModelOpen(false)}
          width={modelData.width}
        />
      )}
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
      />
      <ConfirmBox
        open={confirmBox.status}
        title={confirmBox.title}
        subTitle={confirmBox.subTitle}
        conformSubmit={confirmBox.conformSubmit}
        closeModal={() => setConfirmBox((previous) => ({ ...previous, status: false }))}
        submitLabel={confirmBox.submitLabel}
      />
      {/* =================== PROFILE + INFO GRID =================== */}
      <div className="ec-profile-info">
        <div className="ec-profile-header">
          <div className="profile-avatar-section">
            <div className="avatar-wrapper">
              {basicInformation?.user?.profileImage?.[0]?.signedUrl ? (
                <img
                  src={basicInformation?.user?.profileImage?.[0]?.signedUrl}
                  alt="profile"
                  className="selected-image"
                />
              ) : (
                <img src={profile} alt="profile" />
              )}
            </div>
            <div className="profile-status-badges">
              {consumerStatus && (
                <div className="consumer-status-container">
                  <div className={`consumer-status ${consumerStatus}`}>
                    {consumerStatus === 'VIP' ? (
                      <p className="status-value">{consumerStatus.toUpperCase()}</p>
                    ) : (
                      <p className="status-value">
                        {consumerStatus.charAt(0).toUpperCase() + consumerStatus.slice(1)}
                      </p>
                    )}
                  </div>
                </div>
              )}
              <div className="info-group status">
                <div className={`value ${editForm?.isActive ? 'success' : 'error'}`}>
                  <div className="color-circle" />
                  <p>{editForm?.isActive ? t('active') : t('inactive')}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="profile-menu-action">
            {!isEditMode && menuList.some((item) => item.allow) && (
              <DropDownMenu list={menuList} icon={<MoreIcon />} />
            )}
          </div>
        </div>

        <div className="profile-details-grid">
          {profileStatsConfig.map((item) => (
            <div
              key={item.key}
              className={`stat-card ${item.theme ? `stat-card--${item.theme}` : ''}`}
              title={typeof item.value === 'string' && item.value !== '-' ? item.value : undefined}>
              <div className="icon-wrapper">{item.icon}</div>

              <div className="content">
                <p className="label">{item.label}</p>

                <div className="value">{item.value}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="bottom-actions">
          <MyButton
            label={t('activityLog')}
            buttonType="outline"
            onClickFunc={() => handleLogActivity()}
            svgIcon={<ActivityIcon />}
          />
          <MyButton
            label={t('resetPassword')}
            buttonType="submit"
            onClickFunc={() => resetPasswordFunction()}
            svgIcon={<ResetPasswordIcon />}
          />
        </div>
      </div>

      {/* =================== WALLET =================== */}
      <div className="ec-profile-wallet-container">
        <div className="ec-profile-wallet">
          <div className="wallet-details">
            <div className="text-wrapper">
              <div className="label">{t('walletBalance')}</div>
              <div className="value">
                {ToFixedValue(originalForm?.wallet?.balance) || 0}
                {originalForm?.wallet?.currency ? ` ${originalForm.wallet.currency}` : ''}
              </div>
            </div>
          </div>
          <div className="wallet-actions">
            <MyButton
              label={t('walletHistory')}
              buttonType="outline"
              onClickFunc={renderWalletHistory}
              svgIcon={<CardPosIcon />}
            />
            <MyButton
              label={t('deductMoney')}
              buttonType="error"
              onClickFunc={handleOpenDeductMoneyModal}
              svgIcon={<TrendDownIcon />}
            />
            <MyButton
              label={t('addMoney')}
              buttonType="submit"
              onClickFunc={handleOpenAddMoneyModal}
              svgIcon={<CardPosIcon />}
            />
          </div>
        </div>
      </div>

      {/* =================== QUICK ACTIONS =================== */}
      {/* <div className="bottom-actions">
        <MyButton
          label={t('activityLog')}
          buttonType="outline"
          onClickFunc={() => handleLogActivity()}
          svgIcon={<ActivityIcon />}
        />
        <MyButton
          label={t('resetPassword')}
          buttonType="submit"
          onClickFunc={() => resetPasswordFunction()}
          svgIcon={<ResetPasswordIcon />}
        />
      </div> */}
    </div>
  );
}
